import { useState, useEffect, useCallback } from "react";
import secureLocalStorage from "react-secure-storage";
import { getAuthToken, getClient } from "../utils/apiClient";
import { getSdk } from "../generated/graphql";

export type UserRole = "husband" | "wife";

const ROLE_STORAGE_KEY = "user_role";
const ROLE_CHANGE_EVENT = "role-changed";
const AUTH_STORAGE_KEY = "wordpress-user";
const PUSH_ENDPOINT_STORAGE_KEY = "push_endpoint";

// Global flag to prevent multiple simultaneous syncs across all hook instances
let syncInProgress = false;
let lastSyncedSignature: string | null = null;

export const useRole = () => {
  const [role, setRoleState] = useState<UserRole | null>(() => {
    const stored = secureLocalStorage.getItem(
      ROLE_STORAGE_KEY
    ) as UserRole | null;
    return stored || null; // No default - must be explicitly set
  });

  useEffect(() => {
    const handleRoleChange = (event: Event) => {
      const customEvent = event as CustomEvent<UserRole>;
      setRoleState(customEvent.detail);
    };

    window.addEventListener(ROLE_CHANGE_EVENT, handleRoleChange);
    return () => {
      window.removeEventListener(ROLE_CHANGE_EVENT, handleRoleChange);
    };
  }, []);

  const cachePushEndpoint = useCallback((endpoint: string) => {
    if (!endpoint) return;
    secureLocalStorage.setItem(PUSH_ENDPOINT_STORAGE_KEY, endpoint);
  }, []);

  const getStoredPushEndpoint = useCallback((): string | null => {
    const stored = secureLocalStorage.getItem(PUSH_ENDPOINT_STORAGE_KEY) as
      | string
      | null;
    return stored && stored.length > 0 ? stored : null;
  }, []);

  const resolvePushEndpoint = useCallback(async (): Promise<string | null> => {
    const stored = getStoredPushEndpoint();
    if (stored) {
      return stored;
    }
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
      return null;
    }
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      const endpoint = subscription?.endpoint || null;
      if (endpoint) {
        cachePushEndpoint(endpoint);
      }
      return endpoint;
    } catch (error) {
      console.warn("Unable to resolve push endpoint", error);
      return null;
    }
  }, [cachePushEndpoint, getStoredPushEndpoint]);

  const syncRoleWithBackend = useCallback(
    async (roleToSync: UserRole | null) => {
      if (!roleToSync) {
        return;
      }

      // Only sync for a signed-in user; getClient() below carries the auth.
      if (!getAuthToken()) {
        return;
      }

      const endpoint = await resolvePushEndpoint();

      // Skip sync entirely if no push endpoint - device doesn't have notifications enabled
      // This prevents affecting other devices' notification settings
      if (!endpoint) {
        return;
      }

      const syncSignature = `${roleToSync}:${endpoint}`;

      // Check global flag first to prevent concurrent syncs
      if (syncInProgress || lastSyncedSignature === syncSignature) {
        return;
      }
      syncInProgress = true;

      try {
        await getSdk(getClient()).SetPushExcludedActor({
          excluded_actor: roleToSync,
          endpoint,
        });
        lastSyncedSignature = syncSignature;
      } catch (e) {
        // Allow retries by clearing last synced signature
        lastSyncedSignature = null;
        console.warn("Failed to update push excluded actor:", e);
      } finally {
        syncInProgress = false;
      }
    },
    [resolvePushEndpoint]
  );

  const setRole = async (newRole: UserRole) => {
    setRoleState(newRole);
    secureLocalStorage.setItem(ROLE_STORAGE_KEY, newRole);

    // Dispatch custom event to notify all components using this hook
    const event = new CustomEvent(ROLE_CHANGE_EVENT, { detail: newRole });
    window.dispatchEvent(event);

    await syncRoleWithBackend(newRole);
  };

  // Sync role with backend once on initial mount if role exists
  useEffect(() => {
    if (role) {
      syncRoleWithBackend(role);
    }
    // Only run once on mount, not on role changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleRole = () => {
    if (!role) return; // Can't toggle if no role set
    const newRole: UserRole = role === "husband" ? "wife" : "husband";
    setRole(newRole);
  };

  return { role, setRole, toggleRole };
};
